import React from 'react';
import {
  Box,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { POLISH_GRAPH } from '../polish';
import { BORDER, INK, RADIUS, STATUS_HEX, STATUS_INK, TYPE, type StatusTone } from '../foundation';
import {
  DS2_MOTION,
  DS2_RADIUS,
  DS2_SPACE,
  DS2_TYPE,
  accentMetricSx,
  separatorSx,
  surfaceSx,
  type SurfaceLevel,
} from './tokens';
import { EnsSkeleton } from '../../components/common/EnsLoader';

type SxObject = Record<string, unknown>;
const asSx = (sx: SxProps<Theme> | undefined): SxObject => (sx ?? {}) as SxObject;

/* ── Overline ──────────────────────────────────────────────────────── */

export interface OverlineProps {
  children: React.ReactNode;
  color?: string;
  component?: React.ElementType;
  sx?: SxProps<Theme>;
}

/** Uppercase eyebrow above a title. The one overline style for the app. */
export const Overline: React.FC<OverlineProps> = ({ children, color, component = 'div', sx }) => (
  <Typography component={component} sx={{ ...TYPE.overline, color: color ?? INK.tertiary, ...asSx(sx) }}>
    {children}
  </Typography>
);

/* ── Surface ───────────────────────────────────────────────────────── */

export interface SurfaceProps {
  level?: SurfaceLevel;
  id?: string;
  className?: string;
  sx?: SxProps<Theme>;
  children: React.ReactNode;
  /** Padding preset */
  pad?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  component?: React.ElementType;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

const PAD_MAP = {
  none: 0,
  sm: 1.5,
  md: 2,
  lg: 2.5,
  xl: 4,
} as const;

export const Surface: React.FC<SurfaceProps> = ({
  level = 'flat',
  id,
  className,
  sx,
  children,
  pad = 'md',
  component,
  onClick,
}) => (
  <Box
    id={id}
    className={className}
    component={component ?? 'div'}
    onClick={onClick}
    sx={
      {
        ...(surfaceSx(level) as SxObject),
        p: PAD_MAP[pad],
        minWidth: 0,
        height: '100%',
        ...asSx(sx),
      } as SxProps<Theme>
    }
  >
    {children}
  </Box>
);

/* ── SectionHeader ─────────────────────────────────────────────────── */

export interface SectionHeaderProps {
  /** Overline (eyebrow) above the title */
  label?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  metrics?: React.ReactNode;
  action?: React.ReactNode;
  /** Tighter spacing for nested sections */
  compact?: boolean;
  /** Heading element; page sections are h2, panels h3 */
  as?: 'h2' | 'h3' | 'h4' | 'div';
  /** Title scale: `section` (18px) for page sections, `panel` (15px) inside cards */
  size?: 'section' | 'panel';
  sx?: SxProps<Theme>;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  label,
  title,
  subtitle,
  icon,
  metrics,
  action,
  compact = false,
  as = 'h2',
  size = 'section',
  sx,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: DS2_SPACE.md,
      mb: compact ? 1.5 : 2,
      ...asSx(sx),
    }}
  >
    {icon && (
      <Box
        aria-hidden
        sx={{
          mt: size === 'panel' ? 0.125 : 0.25,
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          '& svg': { fontSize: 20, width: 20, height: 20 },
        }}
      >
        {icon}
      </Box>
    )}
    <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
      {label && <Overline sx={{ mb: 0.5 }}>{label}</Overline>}
      <Typography
        component={as}
        sx={{ ...(size === 'panel' ? TYPE.subheading : TYPE.sectionTitle), color: 'text.primary', m: 0 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ ...TYPE.bodySm, color: 'text.secondary', mt: 0.5, maxWidth: 760 }}>
          {subtitle}
        </Typography>
      )}
      {metrics && (
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ mt: 1.25 }}>
          {metrics}
        </Stack>
      )}
    </Box>
    {action && (
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap gap={1} sx={{ flexShrink: 0 }}>
        {action}
      </Stack>
    )}
  </Box>
);

/* ── Metric / MetricStrip / MetricCard ─────────────────────────────── */

export interface MetricProps {
  label: string;
  value: React.ReactNode;
  suffix?: string;
  color?: string;
  tooltip?: string;
  compact?: boolean;
}

export const Metric: React.FC<MetricProps> = ({
  label,
  value,
  suffix,
  color = 'text.primary',
  tooltip,
  compact,
}) => {
  const inner = (
    <Box sx={{ minWidth: compact ? 96 : 120 }}>
      {label && (
        <Typography component="div" sx={{ ...DS2_TYPE.label, mb: 0.25 }}>
          {label}
        </Typography>
      )}
      <Stack direction="row" alignItems="baseline" spacing={0.5}>
        <Typography component="div" sx={compact ? DS2_TYPE.metricSm : DS2_TYPE.metric} color={color}>
          {value}
        </Typography>
        {suffix && (
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            {suffix}
          </Typography>
        )}
      </Stack>
    </Box>
  );
  if (tooltip) {
    return <Tooltip title={tooltip}>{inner}</Tooltip>;
  }
  return inner;
};

export interface MetricStripProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const MetricStrip: React.FC<MetricStripProps> = ({ children, sx }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    alignItems="flex-end"
    useFlexGap
    gap={{ xs: DS2_SPACE.lg, sm: DS2_SPACE.xl }}
    sx={sx}
  >
    {children}
  </Stack>
);

export interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  /** Secondary line under the value */
  hint?: React.ReactNode;
  /** Colors the value; neutral keeps it ink */
  tone?: StatusTone;
  icon?: React.ReactNode;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}

/** KPI tile — the one metric card for every screen. */
export const MetricCard: React.FC<MetricCardProps> = ({ label, value, hint, tone = 'neutral', icon, onClick, sx }) => {
  const interactive = Boolean(onClick);
  return (
    <Box
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      sx={{
        ...(surfaceSx(interactive ? 'interactive' : 'raised') as SxObject),
        p: 2,
        minHeight: 88,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        ...asSx(sx),
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
        {icon && (
          <Box aria-hidden sx={{ display: 'flex', color: tone === 'neutral' ? INK.tertiary : STATUS_INK[tone], '& svg': { fontSize: 18, width: 18, height: 18 } }}>
            {icon}
          </Box>
        )}
        <Typography component="div" sx={{ ...DS2_TYPE.label, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
      </Stack>
      <Typography
        component="div"
        sx={{ ...TYPE.metricSm, color: tone === 'neutral' ? 'text.primary' : STATUS_INK[tone] }}
      >
        {value}
      </Typography>
      {hint ? (
        <Typography component="div" sx={{ ...TYPE.caption, color: 'text.secondary', mt: 'auto' }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
};

/* ── GraphFrame ────────────────────────────────────────────────────── */

export interface GraphFrameProps {
  title?: string;
  label?: string;
  metrics?: React.ReactNode;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
  minHeight?: number | string;
  maxHeight?: number | string;
  /** Merge into page — no outer card chrome */
  flat?: boolean;
}

export const GraphFrame: React.FC<GraphFrameProps> = ({
  title,
  label,
  metrics,
  toolbar,
  children,
  minHeight = POLISH_GRAPH.minHeight,
  maxHeight = POLISH_GRAPH.maxHeight,
  flat = false,
}) => (
  <Box sx={flat ? { overflow: 'hidden' } : { ...(surfaceSx('flat') as SxObject), overflow: 'hidden' }}>
    {(title || toolbar || metrics) && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: DS2_SPACE.md,
          px: flat ? 0 : 2.5,
          pt: flat ? 0 : 2,
          pb: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {label && <Overline sx={{ mb: 0.5 }}>{label}</Overline>}
          {title && (
            <Typography component="h2" sx={{ ...TYPE.sectionTitle, m: 0 }}>
              {title}
            </Typography>
          )}
          {metrics && <Box sx={{ mt: DS2_SPACE.sm }}>{metrics}</Box>}
        </Box>
        {toolbar}
      </Box>
    )}
    <Box
      sx={{
        ...(flat ? { bgcolor: 'transparent', border: 'none' } : (surfaceSx('diagram') as SxObject)),
        borderRadius: flat ? 0 : `${DS2_RADIUS.md}px`,
        mx: flat ? 0 : 1.5,
        mb: flat ? 0 : 1.5,
        minHeight,
        maxHeight,
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  </Box>
);

/* ── ContextRail (continuity / wayfinding) ─────────────────────────── */

export interface ContextRailMetric {
  label: string;
  value: React.ReactNode;
  color?: string;
}

export interface ContextRailProps {
  phaseLabel: string;
  phaseColor: string;
  metrics: ContextRailMetric[];
  progress?: number;
  indeterminate?: boolean;
  loading?: boolean;
  actions?: React.ReactNode;
}

export const ContextRail: React.FC<ContextRailProps> = ({
  phaseLabel,
  phaseColor,
  metrics,
  progress = 0,
  indeterminate,
  loading,
  actions,
}) => {
  if (loading) {
    return (
      <Box sx={{ py: DS2_SPACE.sm }}>
        <EnsSkeleton shape="block" height={40} />
      </Box>
    );
  }

  return (
    <Box
      className="ds2-context-rail"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: { xs: DS2_SPACE.md, md: DS2_SPACE.lg },
        py: DS2_SPACE.md,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: phaseColor }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', textTransform: 'capitalize' }}>
          {phaseLabel}
        </Typography>
      </Stack>

      <MetricStrip sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
        {metrics.map((m) => (
          <Metric key={m.label} label={m.label} value={m.value} color={m.color ?? 'text.primary'} compact />
        ))}
      </MetricStrip>

      <Box sx={{ flex: '1 1 120px', maxWidth: 200, minWidth: 100 }}>
        <LinearProgress
          variant={indeterminate ? 'indeterminate' : 'determinate'}
          value={progress}
          aria-label={`${phaseLabel} progress`}
          sx={{
            height: 4,
            bgcolor: alpha(phaseColor, 0.14),
            '& .MuiLinearProgress-bar': {
              bgcolor: phaseColor,
              transition: `transform ${DS2_MOTION.slow}`,
            },
          }}
        />
      </Box>

      {actions && (
        <Stack direction="row" alignItems="center" useFlexGap gap={1} flexWrap="wrap" sx={{ flexShrink: 0 }}>
          {actions}
        </Stack>
      )}
    </Box>
  );
};

/* ── Panel (card + header) ─────────────────────────────────────────── */

export interface PanelProps {
  id?: string;
  icon?: React.ReactNode;
  /** Overline above the title */
  label?: string;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  chips?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  level?: SurfaceLevel;
  /** Tighter padding for dense dashboards */
  dense?: boolean;
  /** Hairline between header and body */
  divided?: boolean;
  /** Body has no padding (tables, lists that run edge to edge) */
  flush?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
}

/** The one card-with-header for the whole app. */
export const Panel: React.FC<PanelProps> = ({
  id,
  icon,
  label,
  title,
  subtitle,
  chips,
  actions,
  children,
  level = 'raised',
  dense = false,
  divided = false,
  flush = false,
  className,
  sx,
}) => {
  const pad = dense ? 1.5 : 2.5;
  const hasHeader = Boolean(title || label || actions);
  return (
    <Box
      id={id}
      className={className}
      component="section"
      sx={{ ...(surfaceSx(level) as SxObject), minWidth: 0, overflow: 'hidden', ...asSx(sx) }}
    >
      {hasHeader && (
        <Box
          sx={{
            px: pad,
            pt: dense ? 1.25 : 2,
            pb: divided ? (dense ? 1.25 : 1.5) : 0,
            ...(divided && { borderBottom: `1px solid ${BORDER.subtle}` }),
          }}
        >
          <SectionHeader
            label={label}
            title={title ?? ''}
            subtitle={subtitle}
            icon={icon}
            metrics={chips}
            action={actions}
            as="h3"
            size="panel"
            compact
            sx={{ mb: divided ? 0 : dense ? 1 : 1.5 }}
          />
        </Box>
      )}
      <Box sx={{ px: flush ? 0 : pad, pb: flush ? 0 : dense ? 1.5 : 2.5, pt: hasHeader || flush ? 0 : pad, ...(divided && !flush && { pt: dense ? 1.25 : 2 }) }}>
        {children}
      </Box>
    </Box>
  );
};

/* ── Status dot (for inline text statuses) ─────────────────────────── */

export const StatusDot: React.FC<{ tone?: StatusTone; pulse?: boolean; size?: number }> = ({
  tone = 'neutral',
  pulse,
  size = 8,
}) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: 'inline-block',
      width: size,
      height: size,
      borderRadius: `${RADIUS.pill}px`,
      bgcolor: STATUS_HEX[tone],
      flexShrink: 0,
      ...(pulse && {
        animation: 'ds2StatusPulse 2s ease-in-out infinite',
        '@keyframes ds2StatusPulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.45 },
        },
      }),
    }}
  />
);

export { accentMetricSx, separatorSx, surfaceSx };
